import { SASServer, _removeDepot } from './SASServer';
import { ReportDisplayOptions } from './ReportDisplayOptions';
import { AuthenticationType } from '@sas-dvr/internal-va-react-core/utils/authorization';
import { Connection } from '@sas-dvr/ltjs-transport';
import { LocalServer } from './internal/LocalServer';
import { IServer } from './internal/IServer';

type BaseReportOpts = Readonly<{
  url: string;
  authenticationType?: AuthenticationType;
  reportUri: string;
}>;

export type LoadReportOpts = Readonly<
  {
    id: string;
    displayOptions?: ReportDisplayOptions;
  } & BaseReportOpts
>;

export type LoadReportObjectOpts = Readonly<
  {
    id: string;
    objectName: string;
  } & BaseReportOpts
>;

interface FullConnectionOptions {
  authenticationType: AuthenticationType;
}

export type ConnectionOptions = Readonly<Partial<FullConnectionOptions>>;

const defaultConnectionOptions: FullConnectionOptions = {
  authenticationType: 'credentials',
};

function resolveConnectionOptions(
  url: string,
  options?: ConnectionOptions
): FullConnectionOptions & { serverKey: string } {
  const resolvedOptions: FullConnectionOptions = {
    ...defaultConnectionOptions,
    ...options,
  };
  return {
    ...resolvedOptions,
    serverKey: url,
  };
}

const allServers: Map<string, IServer> = new Map();
const servers: Map<string, SASServer> = new Map();

function addServer(serverKey: string, server: IServer) {
  allServers.set(serverKey, server);
  if (server instanceof SASServer) {
    servers.set(serverKey, server);
  }
}

function removeServer(url: string) {
  allServers.delete(url);
  servers.delete(url);
}

// Generate unique report content Uri's
let reportContentId = 0;
function generateReportContentUri() {
  return `report-content-${reportContentId++}`;
}

let reportStateId = 0;
function generateReportStateUri() {
  return `report-state-${reportStateId++}`;
}

// A map from report content uri to report content
const reportContentMap = new Map<string, string>();

// A map from report content uri to current report's user state
const reportStateMap = new Map<string, { reportUri: string; userState: string }>();

/**
 * Creates a unique reportUri that references the given report content. This reportUri can then be used
 * with a SASReport, SASReportPage, or SASReportObject.
 *
 * @param reportContent - the xml or json string contents of a report
 * @internal
 */
export function createReportUriFromContent(reportContent: string) {
  const uri = generateReportContentUri();
  reportContentMap.set(uri, reportContent);
  return uri;
}

/**
 * Creates a unique reportUri that applies user state content to the given report.
 *
 * @param reportUri - The report's original uri
 * @param userState - User state content string for the report
 * @internal
 */
export function createReportUriFromUserState(reportUri: string, userState: string) {
  const uri = generateReportStateUri();
  reportStateMap.set(uri, { reportUri, userState });
  return uri;
}

type subscribeReportOptions = (
  | {
      url: string;
      reportUri: string;
    }
  | { packageUri: string }
) &
  ConnectionOptions;

export function subscribeReport(options: subscribeReportOptions) {
  if ('packageUri' in options) {
    const serverUrl = new URL(options?.packageUri, window.location.href).href;
    const serverKey = `::offline::${serverUrl}`;
    const server =
      (allServers.get(serverKey) as LocalServer | undefined) || new LocalServer(serverUrl);
    addServer(serverKey, server);
    return server.createReport();
  }
  const stateValues = reportStateMap.get(options.reportUri);
  if (stateValues) {
    return _connectToServer(options.url, options).subscribeWithUserState(
      options.reportUri,
      stateValues.reportUri,
      stateValues.userState
    );
  }
  return _connectToServer(options.url, options).subscribe(
    options.reportUri,
    reportContentMap.get(options.reportUri)
  );
}

export function unsubscribeReport(url: string, reportUri: string, options?: ConnectionOptions) {
  const { serverKey } = resolveConnectionOptions(url, options);
  const server = servers.get(serverKey);
  if (!server) {
    return;
  }
  server.unsubscribe(reportUri);
}

/**
 * @public
 */
export function connectToServer(
  url: string,
  options?: { authenticationType: 'guest' | 'credentials' }
): void {
  _connectToServer(url, options);
}

/**
 * Private version of connectToServer that returns the server object
 */
export function _connectToServer(url: string, options?: ConnectionOptions) {
  const { serverKey, authenticationType } = resolveConnectionOptions(url, options);
  const server =
    servers.get(serverKey) ||
    new SASServer({
      url,
      authenticationType,
    });
  addServer(serverKey, server);
  server.connect();
  return server;
}

export function closeConnection(url: string, options?: ConnectionOptions) {
  const { serverKey } = resolveConnectionOptions(url, options);
  const server = servers.get(serverKey);
  if (server) {
    server.close();
    removeServer(serverKey);
  }
}

export function closeAllConnections() {
  allServers.forEach((server, key) => {
    server.close();
    removeServer(key);
  });
}

export function getServer(url: string, options?: ConnectionOptions) {
  const { serverKey } = resolveConnectionOptions(url, options);
  return servers.get(serverKey);
}

export function getServerForConnection(connection: Connection) {
  for (const [, server] of allServers) {
    if (server.getConnection() === connection) {
      return server;
    }
  }
}

export function getReport(url: string, reportUri: string, options?: ConnectionOptions) {
  const { serverKey } = resolveConnectionOptions(url, options);
  const server = servers.get(serverKey);
  if (!server) {
    return;
  }
  return server.getReport(reportUri);
}

export function reloadAllReports() {
  for (const server of servers.values()) {
    server.reloadAllReports();
  }
}

export async function cleanupTransport() {
  closeAllConnections();
  await _removeDepot();
}
